/**
 * Bounded, deterministic combat-impact presentation.
 *
 * Gameplay emits `fx:impact`; this system copies the pooled payload into its own fixed pools.
 * Age is derived from the authoritative simulation tick, not wall time, so captures and replays
 * see the same effect phase. Rendering never feeds back into combat.
 */

import * as THREE from 'three';
import { clamp, DisposalBag, System, TICK_RATE } from '../core/index.js';

const IMPACT_CAPACITY = 16;
const PARTICLE_CAPACITY = 384;
const FAR_BELOW_STAGE = -1000;
const EPSILON_SCALE = 0.0001;

const STYLES = Object.freeze({
  light: Object.freeze({
    color: 0xffd28b, secondary: 0xff7b39, particles: 7, life: 25,
    core: 0.45, slash: 0.5, ring: 0.75, speed: 1.25, gravity: 1.5,
  }),
  heavy: Object.freeze({
    color: 0xfff0c4, secondary: 0xff6a22, particles: 15, life: 34,
    core: 0.65, slash: 0.45, ring: 0.9, speed: 1.75, gravity: 1.9,
  }),
  counter: Object.freeze({
    color: 0xeafaff, secondary: 0x42dfff, particles: 22, life: 42,
    core: 0.7, slash: 0.55, ring: 1.1, speed: 2.15, gravity: 1.4,
  }),
  guard: Object.freeze({
    color: 0xc8f7ff, secondary: 0x38a7ff, particles: 10, life: 30,
    core: 0.4, slash: 0.25, ring: 1, speed: 1.05, gravity: 0.8,
  }),
  parry: Object.freeze({
    color: 0xffffff, secondary: 0x57f0e8, particles: 16, life: 40,
    core: 0.4, slash: 0.2, ring: 1.4, speed: 1.45, gravity: 0.45,
  }),
  throw: Object.freeze({
    color: 0xffe0ad, secondary: 0xff8a3d, particles: 12, life: 34,
    core: 0.5, slash: 0.35, ring: 0.8, speed: 1.3, gravity: 1.8,
  }),
  concrete: Object.freeze({
    color: 0xd6e0e5, secondary: 0x71808a, particles: 20, life: 54,
    core: 0.25, slash: 0.18, ring: 0.9, speed: 1.65, gravity: 3.2,
  }),
  water: Object.freeze({
    color: 0xbaf8ff, secondary: 0x3dbbd0, particles: 24, life: 48,
    core: 0.15, slash: 0.12, ring: 1.1, speed: 1.5, gravity: 2.8,
  }),
  finisher: Object.freeze({
    color: 0xffffff, secondary: 0x38dff4, particles: 30, life: 62,
    core: 0.75, slash: 0.7, ring: 1.6, speed: 2.6, gravity: 1.2,
  }),
});

function styleFor(kind) {
  if (kind === 'spark-counter') return STYLES.counter;
  if (kind === 'spark-heavy') return STYLES.heavy;
  if (kind === 'guard-flash' || kind === 'guard-break') return STYLES.guard;
  if (kind === 'parry-ring') return STYLES.parry;
  if (kind === 'throw-burst') return STYLES.throw;
  if (kind === 'wall-crack' || kind === 'ground-crack' || kind === 'dust-puff'
      || kind === 'prop-debris') return STYLES.concrete;
  if (kind === 'water-splash' || kind === 'rain-displace' || kind === 'steam-burst') {
    return STYLES.water;
  }
  if (kind === 'finisher-shock') return STYLES.finisher;
  return STYLES.light;
}

function hashText(text) {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function noise01(seed) {
  let x = seed >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d);
  x ^= x >>> 15;
  x = Math.imul(x, 0x846ca68b);
  x ^= x >>> 16;
  return (x >>> 0) / 0x100000000;
}

function channel(hex, shift) {
  return ((hex >>> shift) & 255) / 255;
}

function addWhiteVertexColors(geometry) {
  const count = geometry.attributes.position.count;
  const values = new Float32Array(count * 3);
  values.fill(1);
  geometry.setAttribute('color', new THREE.BufferAttribute(values, 3));
  return geometry;
}

function createBladeGeometry() {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([
    -0.56, 0, 0,
    -0.19, 0.09, 0,
    0.56, 0, 0,
    -0.19, -0.09, 0,
  ], 3));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  geometry.computeVertexNormals();
  return geometry;
}

function resetImpact(slot) {
  slot.birthTick = -1;
  slot.lifeTicks = 1;
  slot.x = 0;
  slot.y = FAR_BELOW_STAGE;
  slot.z = 0;
  slot.angle = 0;
  slot.scale = 0;
  slot.strength = 0;
  slot.ground = false;
  slot.color = 0;
  slot.secondary = 0;
  slot.core = 0;
  slot.slash = 0;
  slot.ring = 0;
}

function resetParticle(p) {
  p.birthTick = -1;
  p.lifeTicks = 1;
  p.x = 0;
  p.y = FAR_BELOW_STAGE;
  p.z = 0;
  p.vx = 0;
  p.vy = 0;
  p.vz = 0;
  p.gravity = 0;
  p.r = 0;
  p.g = 0;
  p.b = 0;
}

export class ImpactFxSystem extends System {
  static id = 'fx';
  static deps = ['render', 'world'];
  static budgetMs = 0.7;

  constructor() {
    super();
    this.bag = new DisposalBag('fx');
    this.scene = null;
    this.group = null;
    this.impacts = null;
    this.particles = null;
    this.coreMesh = null;
    this.slashMesh = null;
    this.crossMesh = null;
    this.ringMesh = null;
    this.particlePoints = null;
    this.positionArray = null;
    this.colorArray = null;
    this._dummy = new THREE.Object3D();
    this._color = new THREE.Color();
    this._unsub = null;
    this._ctx = null;
  }

  async init(ctx) {
    this._ctx = ctx;
    const render = ctx.systems.get('render');
    this.scene = render && render.scene;
    if (!this.scene) throw new Error('fx: render scene is unavailable.');

    this.group = new THREE.Group();
    this.group.name = 'impact-fx';
    this.scene.add(this.group);

    const coreGeometry = this.bag.geometry(addWhiteVertexColors(
      new THREE.OctahedronGeometry(0.09, 0),
    ));
    const slashGeometry = this.bag.geometry(addWhiteVertexColors(createBladeGeometry()));
    const ringGeometry = this.bag.geometry(addWhiteVertexColors(
      new THREE.RingGeometry(0.24, 0.34, 28),
    ));
    const burstMaterial = (opacity) => this.bag.material(new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity,
      // The primitive geometries carry explicit white vertex colors. ANGLE otherwise supplies a
      // missing `color` attribute as zero and multiplies the per-instance tint to black.
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
      toneMapped: false,
      side: THREE.DoubleSide,
    }));

    this.coreMesh = this._makeInstances(coreGeometry, burstMaterial(0.92));
    this.slashMesh = this._makeInstances(slashGeometry, burstMaterial(0.82));
    this.crossMesh = this._makeInstances(slashGeometry, burstMaterial(0.54));
    this.ringMesh = this._makeInstances(ringGeometry, burstMaterial(0.64));

    this.impacts = ctx.pools.create(
      'impact-records',
      () => ({
        birthTick: -1, lifeTicks: 1, x: 0, y: FAR_BELOW_STAGE, z: 0, angle: 0,
        scale: 0, strength: 0, ground: false, color: 0, secondary: 0,
        core: 0, slash: 0, ring: 0,
      }),
      resetImpact,
      IMPACT_CAPACITY,
    );
    this.particles = ctx.pools.create(
      'particles',
      () => ({
        birthTick: -1, lifeTicks: 1, x: 0, y: FAR_BELOW_STAGE, z: 0,
        vx: 0, vy: 0, vz: 0, gravity: 0, r: 0, g: 0, b: 0,
      }),
      resetParticle,
      PARTICLE_CAPACITY,
    );
    this._makeParticleRenderer();

    this._unsub = ctx.events.on('fx:impact', (p) => this._spawn(p, p.tick));
    this.bag.custom(() => this._unsub && this._unsub());
    ctx.render.fx = this;
    this.resetVisuals();
    ctx.log.info(
      `fx: ${IMPACT_CAPACITY} impact records and ${PARTICLE_CAPACITY} particles preallocated`,
    );
  }

  _makeInstances(geometry, material) {
    const mesh = new THREE.InstancedMesh(geometry, material, IMPACT_CAPACITY);
    mesh.name = 'impact-layer';
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.renderOrder = 18;
    this.group.add(mesh);
    return mesh;
  }

  _makeParticleRenderer() {
    const geometry = this.bag.geometry(new THREE.BufferGeometry());
    this.positionArray = new Float32Array(PARTICLE_CAPACITY * 3);
    this.colorArray = new Float32Array(PARTICLE_CAPACITY * 3);
    for (let i = 0; i < PARTICLE_CAPACITY; i++) this.positionArray[i * 3 + 1] = FAR_BELOW_STAGE;
    geometry.setAttribute('position', new THREE.BufferAttribute(this.positionArray, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.colorArray, 3));
    geometry.setDrawRange(0, PARTICLE_CAPACITY);
    const material = this.bag.material(new THREE.PointsMaterial({
      size: 0.075,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.94,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
      toneMapped: false,
    }));
    this.particlePoints = new THREE.Points(geometry, material);
    this.particlePoints.name = 'impact-particles';
    this.particlePoints.frustumCulled = false;
    this.particlePoints.renderOrder = 19;
    this.group.add(this.particlePoints);
  }

  /**
   * Tooling/capture seam. Uses the exact same bounded spawn path as a real combat event.
   */
  previewImpact(spec, tick = 0) {
    this._spawn(spec, tick);
  }

  _spawn(p, tick) {
    if (!this.impacts || !this.particles) return;
    const kind = p.kind || 'spark-light';
    const style = styleFor(kind);
    const strength = clamp(p.strength === undefined ? 1 : p.strength, 0.15, 1.8);
    const reduced = this._ctx.config.get('reduceFlashing');
    const flashScale = reduced ? 0.62 : 1;
    const slot = this.impacts.acquire();
    slot.birthTick = tick | 0;
    slot.lifeTicks = Math.round(style.life * (0.84 + strength * 0.18));
    slot.x = p.x || 0;
    slot.y = p.y || 0;
    slot.z = (p.z || 0) + 0.24;
    slot.angle = Math.atan2(p.ny || 0, p.nx || 1);
    slot.scale = (p.scale === undefined ? 1 : p.scale) * (0.78 + strength * 0.28);
    slot.strength = strength * flashScale;
    slot.ground = kind === 'ground-crack' || kind === 'water-splash'
      || kind === 'rain-displace' || kind === 'dust-puff';
    slot.color = style.color;
    slot.secondary = style.secondary;
    slot.core = style.core;
    slot.slash = style.slash;
    slot.ring = style.ring;

    const qualityScale = clamp(this._ctx.config.quality.particleBudget / 1600, 0.25, 1.35);
    const count = Math.max(3, Math.round(style.particles * qualityScale));
    const seed = (hashText(kind) ^ (tick * 0x9e3779b1)) >>> 0;
    const nx = p.nx || 0;
    const ny = p.ny || (slot.ground ? 1 : 0);
    const nz = p.nz || 0;
    for (let i = 0; i < count; i++) {
      const particle = this.particles.acquire();
      const u = noise01(seed + i * 0x6d2b79f5);
      const v = noise01(seed + i * 0x85ebca6b + 17);
      const angle = u * Math.PI * 2;
      const radial = 0.25 + v * 0.75;
      const speed = style.speed * (0.48 + noise01(seed + i * 97 + 31) * 0.8) * strength;
      particle.birthTick = tick | 0;
      particle.lifeTicks = Math.round(style.life * (0.62 + v * 0.55));
      particle.x = slot.x;
      particle.y = slot.y;
      particle.z = slot.z;
      particle.vx = (Math.cos(angle) * radial + nx * 0.7) * speed;
      particle.vy = (Math.sin(angle) * radial + ny * 0.65 + (slot.ground ? 0.65 : 0)) * speed;
      particle.vz = (noise01(seed + i * 61 + 7) - 0.5 + nz * 0.3) * speed * 0.55;
      particle.gravity = style.gravity;
      const hex = i % 3 === 0 ? style.color : style.secondary;
      particle.r = channel(hex, 16);
      particle.g = channel(hex, 8);
      particle.b = channel(hex, 0);
    }
  }

  update(_dt, ctx) {
    this._updateImpacts(ctx.time.tick);
    this._updateParticles(ctx.time.tick);
    ctx.metrics.setCounter('fx.impactRecords', this.impacts.liveCount);
    ctx.metrics.setCounter('fx.particles', this.particles.liveCount);
  }

  _updateImpacts(tick) {
    for (let i = 0; i < IMPACT_CAPACITY; i++) {
      const slot = this.impacts.itemAt(i);
      let fade = 0;
      if (slot.__poolActive) {
        const age = tick - slot.birthTick;
        if (age > slot.lifeTicks) {
          this.impacts.release(slot);
        } else {
          const phase = clamp(age / slot.lifeTicks, 0, 1);
          fade = (1 - phase) * (1 - phase);
          const bloom = 1 + Math.sin(Math.min(1, phase * 3.2) * Math.PI) * 0.2;
          this._writeImpactInstance(
            this.coreMesh, i, slot, slot.core * slot.scale * fade * bloom, slot.color, 0,
          );
          this._writeImpactInstance(
            this.slashMesh, i, slot, slot.slash * slot.scale * (0.25 + fade * 0.55), slot.color,
            slot.ground ? 0 : slot.angle,
          );
          this._writeImpactInstance(
            this.crossMesh, i, slot,
            slot.slash * slot.scale * (0.14 + fade * 0.34), slot.secondary,
            slot.ground ? Math.PI / 2 : slot.angle + Math.PI / 2,
          );
          this._writeImpactInstance(
            this.ringMesh, i, slot,
            slot.ring * slot.scale * (0.55 + phase * 1.2) * Math.max(0.18, fade),
            slot.secondary, slot.ground ? 0 : slot.angle,
          );
        }
      }
      if (fade === 0) {
        this._hideInstance(this.coreMesh, i);
        this._hideInstance(this.slashMesh, i);
        this._hideInstance(this.crossMesh, i);
        this._hideInstance(this.ringMesh, i);
      }
    }
    this.coreMesh.instanceMatrix.needsUpdate = true;
    this.slashMesh.instanceMatrix.needsUpdate = true;
    this.crossMesh.instanceMatrix.needsUpdate = true;
    this.ringMesh.instanceMatrix.needsUpdate = true;
    if (this.coreMesh.instanceColor) this.coreMesh.instanceColor.needsUpdate = true;
    if (this.slashMesh.instanceColor) this.slashMesh.instanceColor.needsUpdate = true;
    if (this.crossMesh.instanceColor) this.crossMesh.instanceColor.needsUpdate = true;
    if (this.ringMesh.instanceColor) this.ringMesh.instanceColor.needsUpdate = true;
  }

  _writeImpactInstance(mesh, index, slot, scale, hex, rotationZ) {
    const brightness = clamp(slot.strength, 0.18, 1.35);
    this._dummy.position.set(slot.x, slot.y, slot.z);
    this._dummy.rotation.set(slot.ground ? -Math.PI / 2 : 0, 0, rotationZ);
    this._dummy.scale.setScalar(Math.max(EPSILON_SCALE, scale));
    this._dummy.updateMatrix();
    mesh.setMatrixAt(index, this._dummy.matrix);
    this._color.setRGB(
      channel(hex, 16) * brightness,
      channel(hex, 8) * brightness,
      channel(hex, 0) * brightness,
    );
    mesh.setColorAt(index, this._color);
  }

  _hideInstance(mesh, index) {
    this._dummy.position.set(0, FAR_BELOW_STAGE, 0);
    this._dummy.rotation.set(0, 0, 0);
    this._dummy.scale.setScalar(EPSILON_SCALE);
    this._dummy.updateMatrix();
    mesh.setMatrixAt(index, this._dummy.matrix);
    this._color.setRGB(0, 0, 0);
    mesh.setColorAt(index, this._color);
  }

  _updateParticles(tick) {
    const positions = this.positionArray;
    const colors = this.colorArray;
    for (let i = 0; i < PARTICLE_CAPACITY; i++) {
      const p = this.particles.itemAt(i);
      const base = i * 3;
      if (!p.__poolActive) {
        positions[base] = 0;
        positions[base + 1] = FAR_BELOW_STAGE;
        positions[base + 2] = 0;
        colors[base] = 0;
        colors[base + 1] = 0;
        colors[base + 2] = 0;
        continue;
      }
      const ageTicks = tick - p.birthTick;
      if (ageTicks > p.lifeTicks) {
        this.particles.release(p);
        positions[base + 1] = FAR_BELOW_STAGE;
        colors[base] = 0;
        colors[base + 1] = 0;
        colors[base + 2] = 0;
        continue;
      }
      // Start a fraction of a frame away from the origin so the first presented impact contains
      // a readable spray instead of hundreds of coincident points.
      const seconds = (ageTicks + 1.5) / TICK_RATE;
      const phase = clamp(ageTicks / p.lifeTicks, 0, 1);
      const fade = (1 - phase) * (1 - phase);
      positions[base] = p.x + p.vx * seconds;
      positions[base + 1] = p.y + p.vy * seconds - p.gravity * seconds * seconds * 0.5;
      positions[base + 2] = p.z + p.vz * seconds;
      colors[base] = p.r * fade;
      colors[base + 1] = p.g * fade;
      colors[base + 2] = p.b * fade;
    }
    this.particlePoints.geometry.attributes.position.needsUpdate = true;
    this.particlePoints.geometry.attributes.color.needsUpdate = true;
  }

  resetVisuals() {
    if (this.impacts) this.impacts.releaseAll();
    if (this.particles) this.particles.releaseAll();
    if (this.coreMesh) this._updateImpacts(0);
    if (this.particlePoints) this._updateParticles(0);
  }

  dispose() {
    if (this.scene && this.group) this.scene.remove(this.group);
    if (this._ctx) {
      this._ctx.pools.destroy('particles');
      this._ctx.pools.destroy('impact-records');
      if (this._ctx.render.fx === this) this._ctx.render.fx = null;
    }
    this.bag.disposeAll();
    this.scene = null;
    this.group = null;
    this.impacts = null;
    this.particles = null;
    this._ctx = null;
  }
}
